/**
 * Valida RUT chileno (con dígito verificador).
 * Acepta formatos: 12.345.678-9, 12345678-9, 123456789
 */
export const INVALID_RUT_MESSAGE =
	"El número verificador está mal calculado porque el RUT no corresponde a ese número";

/** Texto de ayuda unificado: solo números al escribir; validación y puntos al salir del campo. */
export const RUT_DIGITS_HINT =
	"Solo números, sin puntos ni guion. Al salir del campo o al pulsar Enter se valida y formatea solo.";

export const RUT_INCOMPLETE_MESSAGE =
	"Ingresa el RUT completo (8 u 9 dígitos; si el dígito verificador es K, escribe K al final).";

/**
 * Mientras escribes: solo dígitos (máx. 9) o 7–8 dígitos + K como dígito verificador.
 * No inserta puntos ni guion.
 */
export function sanitizeRutDigitsOnly(value) {
	const raw = String(value ?? "").toUpperCase().replace(/\s/g, "");
	let digits = "";
	for (let i = 0; i < raw.length; i++) {
		const c = raw[i];
		if (c >= "0" && c <= "9") {
			digits += c;
			if (digits.length >= 9) break;
		}
	}
	if (digits.length >= 9) return digits.slice(0, 9);
	if (raw.endsWith("K") && digits.length >= 7 && digits.length <= 8) {
		return `${digits.slice(0, 8)}K`;
	}
	return digits;
}

/**
 * Al salir del campo (blur) o Enter: valida el RUT y devuelve formato 12.345.678-5.
 * @returns {{ ok: true, empty?: boolean, formatted: string, clean?: string, api?: string } | { ok: false, formatted: string, message: string }}
 */
export function commitRutField(raw) {
	const trimmed = String(raw ?? "").trim();
	if (!trimmed) {
		return { ok: true, empty: true, formatted: "" };
	}
	const clean = sanitizeRutInput(trimmed);
	if (clean.length < 8) {
		return {
			ok: false,
			message: RUT_INCOMPLETE_MESSAGE,
			formatted: trimmed,
		};
	}
	const v = validateRut(clean);
	if (!v.valid) {
		return {
			ok: false,
			message: v.message || INVALID_RUT_MESSAGE,
			formatted: trimmed,
		};
	}
	return {
		ok: true,
		formatted: formatRut(clean),
		clean,
		api: toApiRut(clean),
	};
}

/**
 * Login / identificador: true si el texto parece solo RUT (números, puntos, guion, K;
 * sin letras de usuario ni @). Misma heurística que en backoffice para aplicar
 * sanitizeRutDigitsOnly + commitRutField.
 */
export function isLoginRutNumericMode(value) {
	const t = String(value ?? "").trim();
	if (!t || t.includes("@")) return false;
	return !/[a-zA-Z_.]/.test(t);
}

/**
 * Login: si el valor es un RUT (solo dígitos / dígitos+K; puntos y guion se ignoran), formatea al blur.
 * Si quedan letras (nombre de usuario), no modifica.
 * @deprecated Preferir commitRutField + isLoginRutNumericMode en el componente para mostrar errores.
 */
export function maybeFormatRutForLogin(value) {
	const t = String(value ?? "").trim();
	if (!t) return t;
	if (t.includes("@")) return t;
	const cleaned = sanitizeRutInput(t);
	if (cleaned.length < 8) return t;
	if (!/^[\d]+K?$/i.test(cleaned)) return t;
	const r = commitRutField(cleaned);
	if (r.ok && r.formatted) return r.formatted;
	return t;
}

/**
 * Normaliza/sanitiza entrada de RUT para formulario: solo 0-9 y K, sin puntos ni guion.
 * Si se ingresan varias K, conserva solo una al final.
 */
export function sanitizeRutInput(value) {
	const raw = String(value ?? "").toUpperCase();
	const onlyRutChars = raw.replace(/[^0-9K]/g, "");
	const digits = onlyRutChars.replace(/K/g, "");
	const hasK = onlyRutChars.includes("K");
	const normalized = `${digits}${hasK ? "K" : ""}`;
	return normalized.slice(0, 9);
}

export function cleanRut(value) {
	return sanitizeRutInput(value);
}

/**
 * Formato para persistencia/API: sin puntos y con guion antes del DV.
 * Ejemplo: 11.111.111-1 -> 11111111-1
 */
export function toApiRut(value) {
	const cleaned = cleanRut(value);
	if (cleaned.length <= 1) return cleaned;
	const body = cleaned.slice(0, -1);
	const dv = cleaned.slice(-1);
	return `${body}-${dv}`;
}

/**
 * Calcula el dígito verificador de un RUT chileno (solo cuerpo numérico, 7 u 8 dígitos).
 * Misma regla que el SII: pesos 2–7 cíclicos de derecha a izquierda; 11→0, 10→K.
 */
function computeDv(body) {
	const digits = String(body).replace(/\D/g, "");
	if (digits.length < 7 || digits.length > 8) return "";
	let sum = 0;
	let weight = 2;
	for (let i = digits.length - 1; i >= 0; i--) {
		sum += parseInt(digits[i], 10) * weight;
		weight = weight === 7 ? 2 : weight + 1;
	}
	const rest = 11 - (sum % 11);
	if (rest === 11) return "0";
	if (rest === 10) return "K";
	return String(rest);
}

/**
 * DV a partir del cuerpo (7 u 8 dígitos). Vacío si faltan dígitos.
 */
export function computeChileanDv(body) {
	const digits = String(body).replace(/\D/g, "").slice(0, 8);
	if (digits.length < 7) return "";
	return computeDv(digits);
}

/**
 * Formato al escribir: miles con punto y DV automático al completar 7 u 8 dígitos de cuerpo.
 * Cubre RUT de 7 cifras (ej. 1.234.567-K) y de 8 (ej. 12.345.678-5). skipDv: al borrar sin forzar DV.
 */
export function formatRutAsYouType(value, skipDv = false) {
	const raw = (value || "").trim();
	let digits = raw.replace(/\D/g, "").slice(0, 8);

	if (raw.includes("-")) {
		const parts = raw.split("-");
		const leftDigits = (parts[0] || "").replace(/\D/g, "").slice(0, 8);
		const rightChunk = (parts[1] || "")
			.replace(/[^\dKk]/g, "")
			.toUpperCase();
		if (leftDigits.length === 7 && rightChunk.length > 1) {
			digits = `${leftDigits}${rightChunk.slice(1)}`
				.replace(/\D/g, "")
				.slice(0, 8);
		}
	}

	if (digits.length === 0) return "";
	// eslint-disable-next-line security/detect-unsafe-regex
	const withDots = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
	if (skipDv && digits.length >= 7) return withDots + "-";
	if (digits.length >= 7 && raw.endsWith("-")) return withDots + "-";
	if (digits.length < 7) return withDots;
	const dv = computeDv(digits);
	return `${withDots}-${dv}`;
}

/**
 * Valida que el RUT tenga formato correcto y dígito verificador válido.
 * @returns { { valid: boolean, message?: string } }
 */
export function validateRut(value) {
	const cleaned = cleanRut(value);
	if (cleaned.length < 2) {
		return { valid: false, message: "Ingrese un RUT" };
	}
	const body = cleaned.slice(0, -1);
	const dv = cleaned.slice(-1);
	if (!/^\d{7,8}$/.test(body)) {
		return { valid: false, message: INVALID_RUT_MESSAGE };
	}
	if (dv !== "K" && !/^\d$/.test(dv)) {
		return { valid: false, message: INVALID_RUT_MESSAGE };
	}
	const expectedDv = computeDv(body);
	if (expectedDv !== dv) {
		return { valid: false, message: INVALID_RUT_MESSAGE };
	}
	return { valid: true };
}

/** true si está vacío o el RUT es válido (útil en formularios opcionales). */
export function validateRutOrEmpty(value) {
	const cleaned = cleanRut(value);
	if (!cleaned) return true;
	return validateRut(value).valid;
}

/**
 * Formatea un RUT limpio a formato XX.XXX.XXX-X
 */
export function formatRut(value) {
	const cleaned = cleanRut(value);
	if (cleaned.length <= 1) return value;
	const body = cleaned.slice(0, -1);
	const dv = cleaned.slice(-1);
	// eslint-disable-next-line security/detect-unsafe-regex
	const parts = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
	return `${parts}-${dv}`;
}
